/**
 * sanitizeBreadcrumbUrl — Phase 6.
 *
 * Convert an axios-shaped concrete URL into a safe Sentry breadcrumb
 * path. Per the architect's lock-in B6:
 *
 *   1. Drop query string and hash fragments entirely (they may hold
 *      tokens, emails, OTPs, search terms — all PII vectors).
 *   2. Normalize each path segment that LOOKS like an opaque ID
 *      (UUID, ULID, CUID, all-digits, hex >= 16 chars) to ':id'.
 *   3. Preserve the leading method-relative path; never include
 *      origin / baseURL.
 *   4. Cap the resulting string at 200 chars (defense vs runaway
 *      route trees inflating breadcrumb data).
 *   5. Return '/' for empty / non-string input.
 *
 * The function is a pure helper. apiClient calls it before passing
 * the breadcrumb to Sentry.addBreadcrumb. NEVER pass the raw URL
 * to Sentry.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;
const CUID_RE = /^c[a-z0-9]{24,}$/i; // CUID2 / CUID
const NUMERIC_RE = /^\d+$/;
const LONG_HEX_RE = /^[0-9a-f]{16,}$/i;

const MAX_LENGTH = 200;

function looksLikeId(segment: string): boolean {
  return (
    UUID_RE.test(segment) ||
    ULID_RE.test(segment) ||
    CUID_RE.test(segment) ||
    NUMERIC_RE.test(segment) ||
    LONG_HEX_RE.test(segment)
  );
}

export function sanitizeBreadcrumbUrl(rawUrl: unknown): string {
  if (typeof rawUrl !== 'string' || rawUrl.length === 0) return '/';

  // Drop hash + query.
  const noHash = rawUrl.split('#', 1)[0]!;
  const noQuery = noHash.split('?', 1)[0]!;

  // Drop scheme + origin if present (axios may pass full URL when
  // baseURL is concatenated). Keep just the path.
  let path = noQuery;
  const schemeIdx = path.indexOf('://');
  if (schemeIdx !== -1) {
    const afterScheme = path.slice(schemeIdx + 3);
    const slashIdx = afterScheme.indexOf('/');
    path = slashIdx === -1 ? '/' : afterScheme.slice(slashIdx);
  }

  if (path === '' || path === '/') return '/';

  const segments = path.split('/').map((seg) => {
    if (seg === '') return seg;
    return looksLikeId(seg) ? ':id' : seg;
  });
  const normalized = segments.join('/');

  if (normalized.length > MAX_LENGTH) return normalized.slice(0, MAX_LENGTH);
  return normalized;
}
